import { type Domain } from "@/composables/domain-manager";
import { type Process } from "@/composables/process-manager";
import { type Product } from "@/composables/product-manager";
import { Artefact } from "@/lib/artefacts";
import { followLink } from "@/lib/openapi/helpers";
import { Event } from "@/stores/event-store";
import { Flow } from "@/stores/flow-store";
import { Gateway } from "@/stores/gateway-store";
import { type Stek } from "@/stores/steks-store";
import { type Task } from "@/stores/task-store";

export async function getDescriptionArtefact(
    apiEntity: Domain | Product | Process | Task | Gateway | Event | Flow,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (apiEntity.description) {
        let mimetype = apiEntity.description.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(
                `Can only handle markdown descriptions! Not ${mimetype}`
            );
        }

        const { data } = await followLink<File>(
            apiEntity.description,
            "get",
            undefined,
            signal,
            query
        );

        // Assuming data might already be the blob or response data
        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: apiEntity.description.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + "_description",
        });
    }
}

export async function getMissionArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (apiEntity.mission) {
        let mimetype = apiEntity.mission.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(`Can only handle markdown artefacts! Not ${mimetype}`);
        }

        const { data } = await followLink<File>(
            apiEntity.mission,
            "get",
            undefined,
            signal,
            query
        );

        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: apiEntity.mission.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + "_mission",
        });
    }
}

export async function getVisionArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (apiEntity.vision) {
        let mimetype = apiEntity.vision.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(`Can only handle markdown artefacts! Not ${mimetype}`);
        }

        const { data } = await followLink<File>(
            apiEntity.vision,
            "get",
            undefined,
            signal,
            query
        );

        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: apiEntity.vision.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + "_vision",
        });
    }
}

export async function getCoreValuesArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (apiEntity.core_values) {
        let mimetype = apiEntity.core_values.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(`Can only handle markdown artefacts! Not ${mimetype}`);
        }

        const { data } = await followLink<File>(
            apiEntity.core_values,
            "get",
            undefined,
            signal,
            query
        );

        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: apiEntity.core_values.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + "_core_values",
        });
    }
}

export async function getSpecializationsArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (apiEntity.specializations) {
        let mimetype = apiEntity.specializations.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(`Can only handle markdown artefacts! Not ${mimetype}`);
        }

        const { data } = await followLink<File>(
            apiEntity.specializations,
            "get",
            undefined,
            signal,
            query
        );

        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: apiEntity.specializations.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + "_specializations",
        });
    }
}

export async function getTargetMarketsArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (apiEntity.target_markets) {
        let mimetype = apiEntity.target_markets.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(`Can only handle markdown artefacts! Not ${mimetype}`);
        }

        const { data } = await followLink<File>(
            apiEntity.target_markets,
            "get",
            undefined,
            signal,
            query
        );

        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: apiEntity.target_markets.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + "_target_markets",
        });
    }
}
